import { Component, inject } from '@angular/core';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { RadioButtonModule } from 'primeng/radiobutton';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { FileUpload, FileUploadModule } from 'primeng/fileupload';
import { ImageModule } from 'primeng/image';
import { MessageModule } from 'primeng/message';
import { MessageService } from 'primeng/api';
import { UtilityService } from '../../../services/utility.service';
import { GCPImage, ImageCategoryPath, UserProfile } from '../../../constants';
import { FirebaseStorageService } from '../../../services/firebase.storage.service';
import { FirebaseFirestoreService } from '../../../services/firebase.firestore.service';
import { Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs';

@Component({
  selector: 'app-onboarding',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    IftaLabelModule,
    InputTextModule,
    ButtonModule,
    RadioButtonModule,
    FileUploadModule,
    ImageModule,
    MessageModule,
    ProgressSpinnerModule,
  ],
  providers: [FormBuilder, MessageService],
  templateUrl: './onboarding.component.html',
  styleUrl: './onboarding.component.scss'
})  
export class OnboardingComponent {
  messageService = inject(MessageService);
  utilityService = inject(UtilityService);
  formBuilder = inject(FormBuilder);
  firebaseStorageService = inject(FirebaseStorageService);
  firebaseFirestoreService = inject(FirebaseFirestoreService);
  readOnly = false;
  uploadingImagesInProgress = false;
  userProfile: UserProfile | null = null;
  router = inject(Router);
  imageCategoryPaths = ImageCategoryPath;
  private isFormInitialized = false;

  form = this.formBuilder.group({
    fullName: ['', [Validators.required]],
    email: ['', [Validators.required, Validators.email]],
    dateOfBirth: ['', [Validators.required]],
    address: ['', [Validators.required]],
    hasVehicle: ['', [Validators.required]],
    hasVehicleInsurance: ['', [Validators.required]], 
    vehicleType: ['', [Validators.required]],
    groceryShoppingExperience: ['', [Validators.required]],
    hasOtherAppsExperience: ['', [Validators.required]],
  });

  constructor() {
    this.firebaseFirestoreService.subscribeToUserProfile().pipe(
      takeUntilDestroyed(), 
      filter((userProfile) => userProfile !== null))
    .subscribe((userProfile: UserProfile | null) => {
      console.log('userProfile', userProfile);
      this.userProfile = userProfile;
      if(this.userProfile) {
        this.updateForm();
        if (!this.isFormInitialized) {
          this.isFormInitialized = true;
        }
      }
      if(userProfile?.onboardingStatus === 'completed' || userProfile?.onboardingStatus === 'rejected' || userProfile?.onboardingStatus === 'under_review') {
        this.readOnly = true;
      }
    });
  }

  updateForm() {
    if(this.userProfile && !this.isFormInitialized) {
      this.form.patchValue({
        fullName: this.userProfile.onboarding?.fullName,
        email: this.userProfile.onboarding?.email,
        dateOfBirth: this.userProfile.onboarding?.dateOfBirth,
        address: this.userProfile.onboarding?.address,
        hasVehicle: this.userProfile.onboarding?.hasVehicle,
        hasVehicleInsurance: this.userProfile.onboarding?.hasVehicleInsurance,
        vehicleType: this.userProfile.onboarding?.vehicleType,
        groceryShoppingExperience: this.userProfile.onboarding?.groceryShoppingExperience,
        hasOtherAppsExperience: this.userProfile.onboarding?.hasOtherAppsExperience,
      });
    }
  }

  async deleteImage(image: GCPImage, imageCategoryPath: string) {
    console.log(image, imageCategoryPath);
    // remove underscrore and make the next letter capital
    let userObjectKey = imageCategoryPath.split('/').pop()?.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase()).replace(/\s/g, '');
    // make 1st char lowercase
    const firstChar = userObjectKey?.charAt(0).toLowerCase() || '';
    const restOfString = userObjectKey?.slice(1);
    userObjectKey = firstChar + restOfString;

    // remove image from the array
    const images = this.userProfile?.onboarding?.[userObjectKey as keyof UserProfile['onboarding']] || [] as GCPImage[];
    const index = images.indexOf(image);
    if(index > -1) {  
      images.splice(index, 1);
    }
    

    await this.firebaseFirestoreService.updateUserDocument({onboarding: {
      ...this.userProfile?.onboarding,
      [userObjectKey as keyof UserProfile['onboarding']]: images
    }});
    await this.firebaseStorageService.deleteImage(image);
  }

  async uploadImage({files}: {files: Array<File>}, fileupload: FileUpload, imageCategoryPath: string) {
    console.log(files);
    this.uploadingImagesInProgress = true;
    const images = []
    for(let i = 0; i < files.length; i++) {
      const data = await this.firebaseStorageService.uploadImage(files[i], imageCategoryPath);
      if(data) {
        images.push(data);
      }
    }
    fileupload.clear();

    // remove underscrore and make the next letter capital
    let userObjectKey = imageCategoryPath.split('/').pop()?.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase()).replace(/\s/g, '');
    // make 1st char lowercase
    const firstChar = userObjectKey?.charAt(0).toLowerCase() || '';
    const restOfString = userObjectKey?.slice(1);
    userObjectKey = firstChar + restOfString;
    await this.firebaseFirestoreService.updateUserDocument({onboarding: {
      ...this.userProfile?.onboarding,
      [userObjectKey as keyof UserProfile['onboarding']]: [
        ...(this.userProfile?.onboarding?.[userObjectKey as keyof UserProfile['onboarding']] || []),
        ...images
      ]
    }});
    this.uploadingImagesInProgress = false;
  }



  requestCameraPermission() {
    this.utilityService.requestCameraPermission();
  }

  submitOnboarding() {
    if(this.readOnly) {
      return;
    }
    console.log(this.form.value);
    // check if form is valid by marking all fields as touched
    this.form.markAllAsTouched();
    this.form.markAsDirty();
    this.form.updateValueAndValidity();


    const onboarding = this.firebaseFirestoreService.getUserProfile()?.onboarding || {};
    const completed = !!(this.userProfile?.onboarding?.selfie && this.userProfile?.onboarding?.driversLicense && this.userProfile?.onboarding?.vehicleInsurance && this.form.valid);
    console.log(completed);
    // remove undefined values from the form value
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const formValue = Object.fromEntries(Object.entries(this.form.value).filter(([_, value]) => value !== undefined));
    console.log(formValue);
    this.firebaseFirestoreService.updateUserDocument({onboardingCompleted: false, onboardingStatus: completed ? 'under_review' : 'in_progress', onboarding: {
      ...onboarding,
      ...formValue,
      completedOn: completed ? onboarding?.completedOn || Date.now() : null,
      completedOnString: completed ? onboarding?.completedOnString || new Date().toISOString() : null,
      updatedOn: Date.now(),
      updatedOnString: new Date().toISOString(),
      createdOn: onboarding?.createdOn || Date.now(),
      createdOnString: onboarding?.createdOnString || new Date().toISOString(),
    } as UserProfile['onboarding']});

    if(completed) {
      this.readOnly = true;
      // scroll to top of page, smooth scroll
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }
}
